import { Alert, Box, IconButton, Typography } from '@mui/material';
import { Add } from 'iconsax-react';
import { type FC, useCallback, useEffect, useRef } from 'react';

import { ToastAlertColors } from './data/ToastAlertColors';
import { ToastAlertIcons } from './data/ToastAlertIcons';
import { ToastAlertTitles } from './data/ToastAlertTitles';
import type { Toast, ToastContextProps, ToastProviderConfig } from './ToastContext';

interface ToastAlertProps extends Toast {
  removeAlert: ToastContextProps['removeAlert'];
  providerConfig: ToastProviderConfig;
}

export const ToastAlert: FC<ToastAlertProps> = ({
  id,
  title,
  type,
  config,
  providerConfig,
  message,
  action,
  removeAlert,
}) => {
  const alertConfiguration = { ...providerConfig, ...config };

  const timer = useRef<number | null>(null);

  const handleClose = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);

    removeAlert(id);
  }, [removeAlert, id]);

  useEffect(() => {
    if (alertConfiguration.autoDismissDuration > 0) {
      timer.current = window.setTimeout(handleClose, alertConfiguration.autoDismissDuration);
    }

    return (): void => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [handleClose, alertConfiguration.autoDismissDuration]);

  return (
    <Alert
      severity={type}
      icon={ToastAlertIcons[type]}
      sx={{
        width: { xs: 240, sm: 300, md: 360 },
        '& .MuiAlert-action': { m: 0, p: 0.5, ml: 'auto' },
        '& .MuiAlert-message': { p: 0, flex: 1 },
        '& .MuiAlert-icon': { p: 0, py: 0.75, mr: 1 },
      }}
    >
      <Box display='flex' gap={1} alignItems='center'>
        <Typography color={ToastAlertColors[type]}>{title ?? ToastAlertTitles[type]}</Typography>

        <Box display='flex' ml='auto'>
          {action}
          <IconButton color={type} size='small' onClick={handleClose}>
            <Add color={ToastAlertColors[type]} size={24} style={{ transform: 'rotate(45deg)' }} />
          </IconButton>
        </Box>
      </Box>

      <Typography sx={{ wordBreak: 'break-word' }} color={ToastAlertColors[type]} variant='body3'>
        {message}
      </Typography>
    </Alert>
  );
};
